import { REQUIREMENT } from "helpers/constants";
import {
  ParsedImportData,
  RUBRIC_COLUMN_COUNT,
  RequirementImportType,
} from "./types";

export const CSV_DELIMITER = ";";

export const RUBRIC_COLUMN_KEYS = Array.from(
  { length: RUBRIC_COLUMN_COUNT },
  (_, index) => `rubric${index + 1}`,
);

export const RUBRIC_COLUMN_HEADERS = RUBRIC_COLUMN_KEYS.map(
  (key) => `${key} (Skill Level)`,
);

export const EPA_CSV_COLUMNS = [
  "epa_name",
  "epa_description",
  "competencies",
  ...RUBRIC_COLUMN_HEADERS,
];

export const COMPETENCY_CSV_COLUMNS = [
  "name",
  "description",
  ...RUBRIC_COLUMN_HEADERS,
];

export const ITER_CSV_COLUMNS = ["iter_name", "iter_description", "competencies"];

export type ParsedRubricEntry = {
  skill_level: number;
  target_achievement_time: number;
  description: string;
};

export type ParsedRequirementRow = {
  row_key: string;
  name: string;
  description: string;
  competencies: string[];
  rubrics: ParsedRubricEntry[];
};

export function splitCompetencyNames(value: string): string[] {
  return value
    .split(/[\n;]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function getRequirementCsvColumns(
  requirementType: RequirementImportType,
): string[] {
  if (requirementType === REQUIREMENT.REQUIREMENT_EPA) {
    return EPA_CSV_COLUMNS;
  }
  if (requirementType === REQUIREMENT.REQUIREMENT_ITER) {
    return ITER_CSV_COLUMNS;
  }
  return COMPETENCY_CSV_COLUMNS;
}

export function normalizeCsvColumn(column: string): string {
  const trimmed = column.trim();
  const lower = trimmed.toLowerCase();

  if (lower === "epa_name") return "epa_name";
  if (lower === "epa_description") return "epa_description";
  if (lower === "iter_name") return "iter_name";
  if (lower === "iter_description") return "iter_description";
  if (lower === "competencies") return "competencies";
  if (lower === "name") return "name";
  if (lower === "description" || lower === "desc") return "description";

  const rubricMatch = lower.match(/^rubric(\d+)/);
  if (rubricMatch) {
    return `rubric${rubricMatch[1]}`;
  }

  return lower;
}

export function normalizeCsvRow(
  row: Record<string, string>,
): Record<string, string> {
  return Object.entries(row).reduce(
    (acc, [key, value]) => {
      acc[normalizeCsvColumn(key)] = value?.trim() ?? "";
      return acc;
    },
    {} as Record<string, string>,
  );
}

function escapeCsvCell(value: string, delimiter: string): string {
  if (
    value.includes(delimiter) ||
    value.includes('"') ||
    value.includes("\n")
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function generateDelimitedCSV(
  data: Record<string, string>[],
  columns: string[],
  delimiter = CSV_DELIMITER,
): string {
  const header = columns.join(delimiter);
  const rows = data.map((row) =>
    columns
      .map((column) => escapeCsvCell(row[column] ?? "", delimiter))
      .join(delimiter),
  );
  return [header, ...rows].join("\n");
}

export function buildRubricsFromRow(
  row: Record<string, string>,
): ParsedRubricEntry[] {
  const rubrics: ParsedRubricEntry[] = [];

  RUBRIC_COLUMN_KEYS.forEach((key, index) => {
    const description = row[key]?.trim();
    if (!description || description.toLowerCase() === "not applicable") {
      return;
    }

    const sequence = index + 1;
    rubrics.push({
      skill_level: sequence,
      target_achievement_time: sequence,
      description,
    });
  });

  return rubrics;
}

export function getRequirementTemplateDescription(
  requirementType: RequirementImportType,
): string {
  if (requirementType === REQUIREMENT.REQUIREMENT_EPA) {
    return `
One EPA per row in a semicolon-separated CSV (\`;\`).

**Columns:** \`epa_name\`, \`epa_description\`, \`competencies\`, \`rubric1 (Skill Level)\` … \`rubric6 (Skill Level)\`

\`epa_name\` and \`epa_description\` are taken straight from the first two columns. \`competencies\` lists the names of **existing** competencies to link to the EPA (separate multiple names with a new line). Org-exclusive competencies (\`Name [Org Name]\`) are matched first.

Rubric columns hold the EPA milestone descriptions (use \`Not Applicable\` to skip a level). Target achievement time follows column order (rubric1 = time 1, rubric2 = time 2, …). Custom skill level and time labels are set on the Processing step.

Tags, resources, task time, and minimum counts are also set on the Processing step.

When importing from an organization page, each requirement name is saved as \`Name [Org Name]\`.
`;
  }

  if (requirementType === REQUIREMENT.REQUIREMENT_ITER) {
    return `
One ITER per row in a semicolon-separated CSV (\`;\`).

**Columns:** \`iter_name\`, \`iter_description\`, \`competencies\`

\`iter_name\` and \`iter_description\` are taken from the first two columns. \`competencies\` lists the names of **existing** competencies to link to the ITER (separate multiple names with a new line). Org-exclusive competencies (\`Name [Org Name]\`) are matched first.

Tags and resources are set on the Processing step.

When importing from an organization page, each ITER name is saved as \`Name [Org Name]\`.
`;
  }

  return `
One row per Competency in a semicolon-separated CSV (\`;\`).

**Columns:** \`name\`, \`description\`, \`rubric1 (Skill Level)\` … \`rubric6 (Skill Level)\`

Rubric columns contain milestone descriptions; time follows column order (rubric1 = time 1, rubric2 = time 2, …). Custom skill level and time labels are set on the Processing step.

Tags, resources, task time, and minimum counts are set on the Processing step.

When importing from an organization page, each competency name is saved as \`Name [Org Name]\`.
`;
}

export function resolveRequirementName(
  desiredName: string,
  existingNames: Set<string>,
  orgName?: string,
): string {
  const trimmed = desiredName.trim();
  let candidate = trimmed;

  if (orgName) {
    const orgSuffix = ` [${orgName}]`;
    if (!trimmed.toLowerCase().endsWith(orgSuffix.toLowerCase())) {
      candidate = `${trimmed}${orgSuffix}`;
    }
  }

  if (!existingNames.has(candidate.toLowerCase())) {
    return candidate;
  }

  let n = 2;
  let nextCandidate = `${candidate} (${n})`;
  while (existingNames.has(nextCandidate.toLowerCase())) {
    nextCandidate = `${candidate} (${++n})`;
  }
  return nextCandidate;
}

function validateStandardRow(
  row: Record<string, string>,
  rowNum: number,
): string | null {
  if (!row.name?.trim()) {
    return `Row ${rowNum}: name is required`;
  }

  return null;
}

function parseEpaImportCsv(
  rawRows: Record<string, string>[],
): ParsedImportData {
  const errors: string[] = [];
  const requirements: ParsedRequirementRow[] = [];

  const nonEmptyRows = rawRows
    .map(normalizeCsvRow)
    .filter((row) => Object.values(row).some((value) => value?.trim()));

  nonEmptyRows.forEach((row, index) => {
    const rowNum = index + 2;
    const name = row.epa_name?.trim();

    if (!name) {
      errors.push(`Row ${rowNum}: epa_name is required`);
      return;
    }

    requirements.push({
      row_key: `epa-${name}-${index}`,
      name,
      description: row.epa_description?.trim() || "",
      competencies: splitCompetencyNames(row.competencies || ""),
      rubrics: buildRubricsFromRow(row),
    });
  });

  if (requirements.length === 0 && errors.length === 0) {
    errors.push("At least one EPA row is required");
  }

  return { requirements, errors };
}

function parseIterImportCsv(
  rawRows: Record<string, string>[],
): ParsedImportData {
  const errors: string[] = [];
  const requirements: ParsedRequirementRow[] = [];

  const nonEmptyRows = rawRows
    .map(normalizeCsvRow)
    .filter((row) => Object.values(row).some((value) => value?.trim()));

  nonEmptyRows.forEach((row, index) => {
    const rowNum = index + 2;
    const name = row.iter_name?.trim();

    if (!name) {
      errors.push(`Row ${rowNum}: iter_name is required`);
      return;
    }

    requirements.push({
      row_key: `iter-${name}-${index}`,
      name,
      description: row.iter_description?.trim() || "",
      competencies: splitCompetencyNames(row.competencies || ""),
      rubrics: [],
    });
  });

  if (requirements.length === 0 && errors.length === 0) {
    errors.push("At least one ITER row is required");
  }

  return { requirements, errors };
}

function parseCompetencyImportCsv(
  rawRows: Record<string, string>[],
): ParsedImportData {
  const errors: string[] = [];
  const requirements: ParsedRequirementRow[] = [];

  const nonEmptyRows = rawRows
    .map(normalizeCsvRow)
    .filter((row) => Object.values(row).some((value) => value?.trim()));

  nonEmptyRows.forEach((row, index) => {
    const rowNum = index + 2;
    const validationError = validateStandardRow(row, rowNum);

    if (validationError) {
      errors.push(validationError);
      return;
    }

    const name = row.name.trim();
    requirements.push({
      row_key: `req-${name}-${index}`,
      name,
      description: row.description?.trim() || "",
      competencies: [],
      rubrics: buildRubricsFromRow(row),
    });
  });

  return { requirements, errors };
}

export function parseRequirementsCsv(
  rawRows: Record<string, string>[],
  requirementType: RequirementImportType,
): ParsedImportData {
  const rows = rawRows.filter((row) => {
    const first = Object.values(row).find(
      (value) => String(value || "").trim().length > 0,
    );
    return !(first && String(first).trim().startsWith("#"));
  });

  if (requirementType === REQUIREMENT.REQUIREMENT_EPA) {
    return parseEpaImportCsv(rows);
  }
  if (requirementType === REQUIREMENT.REQUIREMENT_ITER) {
    return parseIterImportCsv(rows);
  }

  return parseCompetencyImportCsv(rows);
}

export function getRequirementTemplateContent(
  requirementType: RequirementImportType,
): string {
  if (requirementType === REQUIREMENT.REQUIREMENT_EPA) {
    return generateDelimitedCSV(
      [
        {
          epa_name: "Clinical Reasoning & Decision-making",
          epa_description:
            "Demonstrates critical thinking & problem-solving with consideration of context & constraints",
          competencies: "Info Gathered",
          "rubric1 (Skill Level)":
            "Identifies, organizes, &\ndocuments necessary\ndetails and historic\nelements consistent\nwith common disorders (4)",
          "rubric2 (Skill Level)":
            "Gathers & organizes\npertinent info but may\nomit minor details or\nprovide too much\nunnecessary\ninformation (3)",
          "rubric3 (Skill Level)":
            "Gathers some pertinent\ninfo but may not be\nwell organized or may\nomit details that\nsupport/refute\nproblems (2)",
          "rubric4 (Skill Level)":
            "Gathers insufficient,\nexhaustive, and/or\nirrelevant information (1)",
          "rubric5 (Skill Level)": "Not Applicable",
          "rubric6 (Skill Level)": "Not Applicable",
        },
      ],
      EPA_CSV_COLUMNS,
    );
  }

  if (requirementType === REQUIREMENT.REQUIREMENT_ITER) {
    return generateDelimitedCSV(
      [
        {
          iter_name: "ITER 1: Example milestone",
          iter_description: "Example milestone description",
          competencies: "Info Gathered",
        },
      ],
      ITER_CSV_COLUMNS,
    );
  }

  return generateDelimitedCSV(
    [
      {
        name: "Info Gathered",
        description:
          "Gathers\nrelevant\ninformation\n(Client or\nRecord)",
        "rubric1 (Skill Level)":
          "Identifies, organizes, & documents necessary details and historic elements consistent with common disorders (4)",
        "rubric2 (Skill Level)":
          "Gathers & organizes pertinent info but may omit minor details or provide too much unnecessary information (3)",
        "rubric3 (Skill Level)":
          "Gathers some pertinent info but may not be well organized or may omit details that support/refute problems (2)",
        "rubric4 (Skill Level)":
          "Gathers insufficient, exhaustive, and/or irrelevant information (1)",
        "rubric5 (Skill Level)": "Not Applicable",
        "rubric6 (Skill Level)": "Not Applicable",
      },
    ],
    COMPETENCY_CSV_COLUMNS,
  );
}

export function mapDescriptionToDbFields(
  requirementType: RequirementImportType,
  description: string,
): { description: string | null; overview: string | null } {
  const text = description || null;

  if (
    requirementType === REQUIREMENT.REQUIREMENT_EPA ||
    requirementType === REQUIREMENT.REQUIREMENT_ITER
  ) {
    return { description: text, overview: null };
  }

  return { description: null, overview: text };
}
